import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import api, { TOKEN_KEY, REFRESH_KEY } from './api';
import type { LoginRequest, SignupRequest, JwtPayload } from '@/types/api';

export interface AuthContextValue {
  user: JwtPayload | null;
  token: string | null;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  signup: (data: SignupRequest) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload)) as JwtPayload;
  } catch {
    return null;
  }
}

function isExpired(payload: JwtPayload): boolean {
  return payload.exp * 1000 < Date.now();
}

const STORAGE_KEY = TOKEN_KEY;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const payload = decodeJwt(stored);
      if (payload && !isExpired(payload)) {
        // Access token still valid — restore immediately
        setToken(stored);
        setUser(payload);
        setIsLoading(false);
      } else if (localStorage.getItem(REFRESH_KEY)) {
        // Access token expired but refresh token exists — silently refresh
        api.post<{ access_token: string; refresh_token: string }>('/auth/refresh', {
          refresh_token: localStorage.getItem(REFRESH_KEY),
        }).then((res) => {
          const { access_token, refresh_token } = res.data;
          localStorage.setItem(STORAGE_KEY, access_token);
          localStorage.setItem(REFRESH_KEY, refresh_token);
          setToken(access_token);
          setUser(decodeJwt(access_token));
        }).catch(() => {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(REFRESH_KEY);
        }).finally(() => setIsLoading(false));
        return; // Don't set isLoading=false below — the promise handles it
      } else {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    const res = await api.post<{ access_token: string; refresh_token: string }>('/auth/login', data);
    const { access_token, refresh_token } = res.data;
    localStorage.setItem(STORAGE_KEY, access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
    setToken(access_token);
    setUser(decodeJwt(access_token));
  }, []);

  const signup = useCallback(async (data: SignupRequest) => {
    // Signup sends verification email — no auto-login
    await api.post('/auth/signup', data);
    // Store email for the check-email page to use
    sessionStorage.setItem('forgealpha_verify_email', data.email);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    try { await api.post('/auth/logout', { refresh_token: refreshToken }); } catch {}
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
